import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, type ReactNode } from "react";
import { listContacts, listUnreadCountsByPeer } from "../../lib/db";
import { postStyxExtensionBadgeTotal } from "../../lib/styxExtension";
import { broadcastTabSync } from "../../lib/tabSync";
import {
  contactsReducer,
  initialContactsState,
  type ContactsAction,
  type ContactsState,
} from "../state/contactsState";
import { useSessionResetRegistry } from "../SessionResetContext";
import { useAuth } from "./AuthContext";

type ContactsContextValue = {
  contactsState: ContactsState;
  dispatchContacts: React.Dispatch<ContactsAction>;
  refreshContacts: () => Promise<void>;
  refreshUnreadCounts: () => Promise<void>;
  notifyContactsChanged: () => void;
};

const ContactsContext = createContext<ContactsContextValue | null>(null);

export function ContactsProvider({ children }: { children: ReactNode }) {
  const { register } = useSessionResetRegistry();
  const [contactsState, dispatchContacts] = useReducer(contactsReducer, undefined, initialContactsState);
  const { sodiumReady, identityRow } = useAuth();

  useEffect(() => {
    return register(() => {
      dispatchContacts({ type: "reset" });
    });
  }, [register]);

  const refreshUnreadCounts = useCallback(async () => {
    const counts = await listUnreadCountsByPeer();
    dispatchContacts({ type: "set-unread-counts", counts });
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    postStyxExtensionBadgeTotal(total);
    if ("setAppBadge" in navigator && typeof navigator.setAppBadge === "function") {
      try {
        if (total === 0) {
          await navigator.clearAppBadge();
        } else {
          await navigator.setAppBadge(total);
        }
      } catch {
        /* ignore */
      }
    }
  }, []);

  const refreshContacts = useCallback(async () => {
    const contacts = await listContacts();
    contacts.sort((a, b) => a.username.localeCompare(b.username));
    dispatchContacts({ type: "set-contacts", contacts });
  }, []);

  const notifyContactsChanged = useCallback(() => {
    broadcastTabSync({ type: "contacts-changed" });
    broadcastTabSync({ type: "idb-changed", scope: "contacts" });
  }, []);

  useEffect(() => {
    if (sodiumReady && identityRow) {
      void refreshContacts();
      void refreshUnreadCounts();
    }
  }, [identityRow, refreshContacts, refreshUnreadCounts, sodiumReady]);

  const value = useMemo(
    () => ({
      contactsState,
      dispatchContacts,
      refreshContacts,
      refreshUnreadCounts,
      notifyContactsChanged,
    }),
    [contactsState, refreshContacts, refreshUnreadCounts, notifyContactsChanged],
  );

  return <ContactsContext.Provider value={value}>{children}</ContactsContext.Provider>;
}

export function useContacts(): ContactsContextValue {
  const ctx = useContext(ContactsContext);
  if (!ctx) throw new Error("useContacts outside ContactsProvider");
  return ctx;
}
